import React from 'react'
import {
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  useMediaQuery,
} from '@mui/material'
import { styled, useTheme } from '@mui/material/styles'
import { FinancialsGrowthResponseData, GrowthRecord } from '../Config/types'
import { getIndicatorColorForValue, tableColors } from '../Config/colors'

type Props = {
  data: FinancialsGrowthResponseData;
  dataField: 'mean_growth_rate' | 'growth_rate';
  ariaLabel?: string;
  sortOrder?: 'asc' | 'desc';
};

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  padding: '10px',
  border: '1px solid #ccc',
  textAlign: 'left',
}));

const CenterAlignTableCell = styled(StyledTableCell)({
  padding: '5px',
  textAlign: 'center',
});

const StickyCompanyCell = styled(StyledTableCell)(({ theme }) => ({
  position: 'sticky',
  left: 0,
  backgroundColor: tableColors.describingCellBackground,
  zIndex: 2,
  width: '100px',
  fontWeight: 'bold',
  verticalAlign: 'middle',
  border: '1px solid #ccc',
  boxShadow: '2px 0 5px rgba(0, 0, 0, 0.1)',
}));

const StickyMetricCell = styled(StyledTableCell)(({ theme }) => ({
  position: 'sticky',
  left: '100px',
  backgroundColor: tableColors.describingCellBackground,
  zIndex: 1,
  width: '150px',
  border: '1px solid #ccc',
  boxShadow: '2px 0 5px rgba(0, 0, 0, 0.1)',
}));

const StickyHeaderCompanyCell = styled(TableCell)(({ theme }) => ({
  position: 'sticky',
  top: 0,
  left: 0,
  zIndex: theme.zIndex.appBar - 1,
  backgroundColor: tableColors.headerCellBackground,
  width: '100px',
  fontWeight: 'bold',
  border: '1px solid #ccc',
  boxShadow: '2px 0 5px rgba(0, 0, 0, 0.1)',
  [theme.breakpoints.down('md')]: {
    zIndex: 3,
  },
}));

const StickyHeaderMetricCell = styled(TableCell)(({ theme }) => ({
  position: 'sticky',
  top: 0,
  left: '100px',
  zIndex: theme.zIndex.appBar - 2,
  backgroundColor: tableColors.headerCellBackground,
  width: '150px',
  fontWeight: 'bold',
  border: '1px solid #ccc',
  boxShadow: '2px 0 5px rgba(0, 0, 0, 0.1)',
  [theme.breakpoints.down('md')]: {
    zIndex: 2,
  },
}));

const CenterAlignedHeaderCell = styled(TableCell)(({ theme }) => ({
  textAlign: 'center',
  position: 'sticky',
  top: 0,
  zIndex: theme.zIndex.appBar - 3,
  backgroundColor: tableColors.headerCellBackground,
  border: '1px solid #ccc',
  fontWeight: 'bold',
  [theme.breakpoints.down('md')]: {
    zIndex: 1,
  },
}));

const CompanyRowSeparator = styled(TableRow)({
  '& td': {
    height: 0,
    padding: 0,
    margin: 0,
    border: 'none',
    borderBottom: '1px solid #ccc',
    lineHeight: 0,
  },
});

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  // Additional styles if needed
}));

const GrowthRateTable: React.FC<Props> = ({ data, dataField, ariaLabel = 'Growth Table', sortOrder = 'asc' }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  console.log("Mobile view active: " + isMobile)

  // Collect all unique periods across all companies and sort them
  const allPeriods = new Set<number>();
  data.financials_growths.forEach(company => {
    company[dataField].forEach(record => {
      allPeriods.add(record.period);
    });
  });
  const sortedPeriods = Array.from(allPeriods).sort((a, b) => 
    sortOrder === 'asc' ? a - b : b - a
  );

  // Helper function to find growth record for a specific period
  const findRecordForPeriod = (records: GrowthRecord[], period: number): GrowthRecord | null => {
    return records.find(record => record.period === period) || null;
  };

  // Responsive maxHeight: smaller on mobile to show multiple tables
  const tableMaxHeight = isMobile ? '50vh' : '90vh';

  return (
    <TableContainer component={Paper} sx={{ maxHeight: tableMaxHeight, overflowX: 'auto' }}>
      <Table
        stickyHeader
        sx={{ tableLayout: 'auto', borderCollapse: 'collapse' }}
        aria-label={ariaLabel}
      >
        <TableHead>
          <TableRow>
            <StickyHeaderCompanyCell>Company</StickyHeaderCompanyCell>
            <StickyHeaderMetricCell>Metric</StickyHeaderMetricCell>
            {sortedPeriods.map((period, index) => (
              <CenterAlignedHeaderCell key={index}>{period}</CenterAlignedHeaderCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {data.financials_growths.map((company, companyIndex) => (
            <React.Fragment key={company.symbol}>
              {/* Row separator between companies */}
              {companyIndex !== 0 && (
                <CompanyRowSeparator>
                  <TableCell colSpan={sortedPeriods.length + 2} />
                </CompanyRowSeparator>
              )}
              {/* Equity Row */}
              <StyledTableRow>
                <StickyCompanyCell rowSpan={4}>{company.symbol}</StickyCompanyCell>
                <StickyMetricCell>Equity</StickyMetricCell>
                {sortedPeriods.map((period, index) => {
                  const record = findRecordForPeriod(company[dataField], period);
                  if (record) {
                    const indicatorStyle = getIndicatorColorForValue(record.equity);
                    return (
                      <CenterAlignTableCell key={index} sx={indicatorStyle}>
                        {(record.equity * 100).toFixed(2)}%
                      </CenterAlignTableCell>
                    );
                  }
                  return (
                    <CenterAlignTableCell key={index} sx={{ color: 'text.disabled' }}>
                      N/A
                    </CenterAlignTableCell>
                  );
                })}
              </StyledTableRow>
              {/* Free Cash Flow Row */}
              <StyledTableRow>
                <StickyMetricCell>Free Cash Flow</StickyMetricCell>
                {sortedPeriods.map((period, index) => {
                  const record = findRecordForPeriod(company[dataField], period);
                  if (record) {
                    const indicatorStyle = getIndicatorColorForValue(record.free_cash_flow);
                    return (
                      <CenterAlignTableCell key={index} sx={indicatorStyle}>
                        {(record.free_cash_flow * 100).toFixed(2)}%
                      </CenterAlignTableCell>
                    );
                  }
                  return (
                    <CenterAlignTableCell key={index} sx={{ color: 'text.disabled' }}>
                      N/A
                    </CenterAlignTableCell>
                  );
                })}
              </StyledTableRow>
              {/* Revenue Row */}
              <StyledTableRow>
                <StickyMetricCell>Revenue</StickyMetricCell>
                {sortedPeriods.map((period, index) => {
                  const record = findRecordForPeriod(company[dataField], period);
                  if (record) {
                    const indicatorStyle = getIndicatorColorForValue(record.revenue);
                    return (
                      <CenterAlignTableCell key={index} sx={indicatorStyle}>
                        {(record.revenue * 100).toFixed(2)}%
                      </CenterAlignTableCell>
                    );
                  }
                  return (
                    <CenterAlignTableCell key={index} sx={{ color: 'text.disabled' }}>
                      N/A
                    </CenterAlignTableCell>
                  );
                })}
              </StyledTableRow>
              {/* Earnings Per Share (Diluted) Row */}
              <StyledTableRow>
                <StickyMetricCell>Earnings Per Share (Diluted)</StickyMetricCell>
                {sortedPeriods.map((period, index) => {
                  const record = findRecordForPeriod(company[dataField], period);
                  if (record) {
                    const indicatorStyle = getIndicatorColorForValue(record.earnings_per_share_diluted);
                    return (
                      <CenterAlignTableCell key={index} sx={indicatorStyle}>
                        {(record.earnings_per_share_diluted * 100).toFixed(2)}%
                      </CenterAlignTableCell>
                    );
                  }
                  return (
                    <CenterAlignTableCell key={index} sx={{ color: 'text.disabled' }}>
                      N/A
                    </CenterAlignTableCell>
                  );
                })}
              </StyledTableRow>
            </React.Fragment>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default GrowthRateTable;
